import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, filter, Observable, switchMap, take, tap } from 'rxjs';

import { Content, ContentVisibility, type Folder, FolderType } from '@efficiently/models';

import { ContentsRepository } from '@efficiently/client-api-content';
import { LoadingScope, LoadingService } from '@efficiently/client-core-loading';

import { ContentUploadStore, UploadContentsArgs } from '../api-content/content-store';
import { FolderStore } from '../api-folders/folder-store';
import { CONFIGURATOR_LOADING_SCOPES } from '../configurator-loading-scopes.constant';

type ContentUploadState = {
	error: string | null;
	lastUploadedContents: Content[];
};

/**
 * Concrete {@link ContentUploadStore} backed by {@link ComponentStore}. Inject
 * the abstract class so the ComponentStore API stays hidden from consumers.
 */
@Injectable()
export class ContentUploadStoreService extends ComponentStore<ContentUploadState> implements ContentUploadStore {
	private readonly contentsRepository = inject(ContentsRepository);
	private readonly loadingService = inject(LoadingService);
	private readonly folderStore = inject(FolderStore);

	constructor() {
		super({
			error: null,
			lastUploadedContents: [],
		});
	}

	public readonly uploadCompleted$: Observable<Content[]> = this.select(state => state.lastUploadedContents).pipe(
		filter(contents => contents.length > 0),
		tap(() => this.patchState({ lastUploadedContents: [] })),
	);

	public readonly error$ = this.select(state => state.error);

	/**
	 * Waits until folders are loaded, resolves the target folder by type, then
	 * delegates to `uploadContents`. Wrapped in a loading scope so the spinner
	 * covers the (possibly non-instant) folder wait on the first upload.
	 */
	private readonly uploadContentEffect = this.effect(
		(trigger$: Observable<{ file: File; folderType: FolderType; visibility?: ContentVisibility }>) =>
			trigger$.pipe(
				switchMap(({ file, folderType, visibility }) =>
					this.loadingService.doLoading(
						this.folderStore.folders$.pipe(
							filter(folders => folders.length > 0),
							take(1),
							tap(folders => {
								const folder = this.getFolderByType(folders, folderType);
								this.uploadContents({
									files: [file],
									folderId: folder._id,
									folderParent: folderType,
									visibility,
								});
							}),
							catchError(error => {
								const message = error instanceof Error ? error.message : 'Failed to upload content';
								this.patchState({ error: message });
								return EMPTY;
							}),
						),
						{ scope: this.getUploadScope(folderType) },
					),
				),
			),
	);

	/**
	 * Delegates to the repository, writes results to `lastUploadedContents` on
	 * success, or captures the message into `error` on failure.
	 */
	private readonly uploadContentsEffect = this.effect((trigger$: Observable<UploadContentsArgs>) =>
		trigger$.pipe(
			tap(() => this.patchState({ error: null })),
			switchMap(args =>
				this.loadingService.doLoading(
					this.contentsRepository
						.uploadContents(
							args.files,
							args.projectId,
							args.projectOrgId,
							args.projectOrgCode,
							args.folderId,
							args.folderParent,
							args.parentId,
							args.parentType,
							args.documentType,
							args.visibility,
							args.modifier,
						)
						.pipe(
							tap(uploadedContents => {
								this.patchState({ lastUploadedContents: uploadedContents });
							}),
							catchError(error => {
								const message = error instanceof Error ? error.message : 'Failed to upload content';
								this.patchState({ error: message });
								return EMPTY;
							}),
						),
					{ scope: this.getUploadScope(args.folderParent) },
				),
			),
		),
	);

	public uploadContent(file: File, folderType: FolderType, visibility?: ContentVisibility): void {
		this.uploadContentEffect({ file, folderType, visibility });
	}

	public uploadContents(args: UploadContentsArgs): void {
		this.uploadContentsEffect(args);
	}

	private getFolderByType(folders: Folder[], folderType: FolderType): Folder {
		const folder = folders.find(item => item.type === folderType);

		if (!folder) {
			throw new Error(`Folder of type "${folderType}" not found`);
		}

		return folder;
	}

	private getUploadScope(folderParent?: string): LoadingScope {
		if (folderParent === FolderType.Images) {
			return CONFIGURATOR_LOADING_SCOPES.upload.image;
		}

		if (folderParent === FolderType.Logos) {
			return CONFIGURATOR_LOADING_SCOPES.upload.logo;
		}

		return CONFIGURATOR_LOADING_SCOPES.upload.default;
	}
}
